<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.statusList.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button v-if="!isTemplateMode" variant="primary" size="sm" @click="saveStatusList" :disabled="isSaving">
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.statusList.loading')" />
            <span class="ml-2">{{ $t("dmp.statusList.loading") }}...</span>
          </div>

          <b-form v-else @submit.prevent="saveStatusList">
            <b-row>
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.statusList.optionsSection") }}
                  </h6>

                  <b-row>
                    <!-- System Trouble Status Monitors -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.systemTroubleStatusMonitors')">
                        <b-form-group :label="$t('dmp.statusList.systemTroubleStatusMonitors')"
                          label-for="system-trouble-status-monitors">
                          <b-form-input id="system-trouble-status-monitors"
                            v-model="statusList.systemTroubleStatusMonitors"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Fire Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext" :name="$t('dmp.statusList.fireZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.fireZoneKeypads')" label-for="fire-zone-keypads">
                          <b-form-input id="fire-zone-keypads" v-model="statusList.fireZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Carbon Monoxide Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.carbonMonoxideZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.carbonMonoxideZoneKeypads')"
                          label-for="carbon-monoxide-zone-keypads">
                          <b-form-input id="carbon-monoxide-zone-keypads" v-model="statusList.carbonMonoxideZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Burglary Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.burglaryZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.burglaryZoneKeypads')"
                          label-for="burglary-zone-keypads">
                          <b-form-input id="burglary-zone-keypads" v-model="statusList.burglaryZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Supervisory Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.supervisoryZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.supervisoryZoneKeypads')"
                          label-for="supervisory-zone-keypads">
                          <b-form-input id="supervisory-zone-keypads" v-model="statusList.supervisoryZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Panic Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext" :name="$t('dmp.statusList.panicZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.panicZoneKeypads')" label-for="panic-zone-keypads">
                          <b-form-input id="panic-zone-keypads" v-model="statusList.panicZoneKeypads"
                            :state="getValidationState(validationContext)"/>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Emergency Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.emergencyZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.emergencyZoneKeypads')"
                          label-for="emergency-zone-keypads">
                          <b-form-input id="emergency-zone-keypads" v-model="statusList.emergencyZoneKeypads"
                            :state="getValidationState(validationContext)"/>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Auxiliary 1 Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.auxiliary1ZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.auxiliary1ZoneKeypads')"
                          label-for="auxiliary1-zone-keypads">
                          <b-form-input id="auxiliary1-zone-keypads" v-model="statusList.auxiliary1ZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Auxiliary 2 Zone Keypads -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext"
                        :name="$t('dmp.statusList.auxiliary2ZoneKeypads')">
                        <b-form-group :label="$t('dmp.statusList.auxiliary2ZoneKeypads')"
                          label-for="auxiliary2-zone-keypads">
                          <b-form-input id="auxiliary2-zone-keypads" v-model="statusList.auxiliary2ZoneKeypads"
                            :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Comm Path Trouble -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider #default="validationContext" :name="$t('dmp.statusList.commPathTrouble')"
                        rules="required">
                        <b-form-group :label="$t('dmp.statusList.commPathTrouble')" label-for="comm-path-trouble">
                          <b-form-select id="comm-path-trouble" v-model="statusList.commPathTrouble"
                            :options="commPathTroubleOptions" :state="getValidationState(validationContext)" />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, inject, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "DmpPanelsConfigStatusList",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    statusListData: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const { refFormObserver, getValidationState } = formValidation();

    const isSaving = ref(false);
    const isLoading = ref(false);

    const statusList = ref({
      auxiliary1ZoneKeypads: "",
      auxiliary2ZoneKeypads: "",
      burglaryZoneKeypads: "",
      carbonMonoxideZoneKeypads: "",
      commPathTrouble: "N",
      emergencyZoneKeypads: "",
      fireZoneKeypads: "",
      panicZoneKeypads: "",
      supervisoryZoneKeypads: "",
      systemTroubleStatusMonitors: "",
    });


    const commPathTroubleOptions = computed(() => [
      { value: "YES", text: proxy.$t("dmp.statusList.yes") },
      { value: "NO", text: proxy.$t("dmp.statusList.no") },
      { value: "ALL", text: proxy.$t("dmp.statusList.all") },
    ]);

    const normalizeZoneRestoralsToBack = (value) => {
      const map = {
        Y: "YES",
        N: "NO",
        A: "ALL",
      };
      return map[value] ?? "YES";
    };


    const loadStatusList = () => {
      if (props.statusListData && Object.keys(props.statusListData).length > 0) {
        statusList.value = {
          auxiliary1ZoneKeypads: props.statusListData.auxiliary1ZoneKeypads ?? "",
          auxiliary2ZoneKeypads: props.statusListData.auxiliary2ZoneKeypads ?? "",
          burglaryZoneKeypads: props.statusListData.burglaryZoneKeypads ?? "",
          carbonMonoxideZoneKeypads: props.statusListData.carbonMonoxideZoneKeypads ?? "",
          commPathTrouble: normalizeZoneRestoralsToBack(props.statusListData.commPathTrouble),
          emergencyZoneKeypads: props.statusListData.emergencyZoneKeypads ?? "",
          fireZoneKeypads: props.statusListData.fireZoneKeypads ?? "",
          panicZoneKeypads: props.statusListData.panicZoneKeypads ?? "",
          supervisoryZoneKeypads: props.statusListData.supervisoryZoneKeypads ?? "",
          systemTroubleStatusMonitors: props.statusListData.systemTroubleStatusMonitors ?? "",
        };
      }
    };

    // Construye la sección en forma GET para el template. El GET conserva los
    // códigos cortos (Y/N/A); el form maneja los valores legibles (inverso de
    // normalizeZoneRestoralsToBack).
    const buildTemplateItem = () => {
      const toCode = { YES: "Y", NO: "N", ALL: "A" };
      return {
        ...(props.statusListData || {}),
        auxiliary1ZoneKeypads: statusList.value.auxiliary1ZoneKeypads,
        auxiliary2ZoneKeypads: statusList.value.auxiliary2ZoneKeypads,
        burglaryZoneKeypads: statusList.value.burglaryZoneKeypads,
        carbonMonoxideZoneKeypads: statusList.value.carbonMonoxideZoneKeypads,
        commPathTrouble: toCode[statusList.value.commPathTrouble] ?? statusList.value.commPathTrouble,
        emergencyZoneKeypads: statusList.value.emergencyZoneKeypads,
        fireZoneKeypads: statusList.value.fireZoneKeypads,
        panicZoneKeypads: statusList.value.panicZoneKeypads,
        supervisoryZoneKeypads: statusList.value.supervisoryZoneKeypads,
        systemTroubleStatusMonitors: statusList.value.systemTroubleStatusMonitors,
      };
    };

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const saveStatusList = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        emitTemplateUpdate("status_list", buildTemplateItem());
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.statusList.savedSuccessfully"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      try {
        const payload = {
          auxiliary1ZoneKeypads: statusList.value.auxiliary1ZoneKeypads,
          auxiliary2ZoneKeypads: statusList.value.auxiliary2ZoneKeypads,
          burglaryZoneKeypads: statusList.value.burglaryZoneKeypads,
          carbonMonoxideZoneKeypads: statusList.value.carbonMonoxideZoneKeypads,
          commPathTrouble: statusList.value.commPathTrouble,
          emergencyZoneKeypads: statusList.value.emergencyZoneKeypads,
          fireZoneKeypads: statusList.value.fireZoneKeypads,
          panicZoneKeypads: statusList.value.panicZoneKeypads,
          supervisoryZoneKeypads: statusList.value.supervisoryZoneKeypads,
          systemTroubleStatusMonitors: statusList.value.systemTroubleStatusMonitors,
        };

        await store.dispatch("dmp_panels/setStatusList", {
          uuid: props.uuid,
          statusListData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.statusList.savedSuccessfully"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("operation-completed");
      } catch (error) {
        console.error("Error saving status list:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.statusList.errorSaving"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSaving.value = false;
      }
    };

    watch(
      () => props.statusListData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadStatusList();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadStatusList();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("status_list", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("status_list");
      }
    });

    return {
      refFormObserver,
      getValidationState,
      isSaving,
      isLoading,
      statusList,
      commPathTroubleOptions,
      saveStatusList,
      isTemplateMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid #ebe9f1 !important;
}

.dark-layout {
  .border {
    border-color: #404656 !important;
  }
}
</style>